import React, { useState, useEffect, useRef } from 'react';

const AppointmentFilter = ({ initialFilters = {}, onFilterChange, isVisible = true, onClose }) => {
    const filterRef = useRef(null);
    
    // State to match the fields in AppointmentTable with enhanced date range support
    const [filters, setFilters] = useState({
        id: initialFilters.id || '',
        appointmentId: initialFilters.appointmentId || '',
        patientName: initialFilters.patientName || '',
        patientAvatar: initialFilters.patientAvatar || '',
        dateOfBirth: initialFilters.dateOfBirth || '',
        address: initialFilters.address || '',
        userid: initialFilters.userid || '',
        meetinguserid: initialFilters.meetinguserid || '',
        doctorName: initialFilters.doctorName || '',
        doctorRole: initialFilters.doctorRole || '',
        date: initialFilters.date || '',
        dateRange: initialFilters.dateRange || 'specific', // 'specific', 'today', 'week', 'month', 'custom'
        dateFrom: initialFilters.dateFrom || '',
        dateTo: initialFilters.dateTo || '',
        startTime: initialFilters.startTime || '',
        endTime: initialFilters.endTime || '',
        status: initialFilters.status || '',
        medicalHistory: initialFilters.medicalHistory || '',
        appointmentType: initialFilters.appointmentType || '',
        notes: initialFilters.notes || '',
        checkInTime: initialFilters.checkInTime || '',
        waitTime: initialFilters.waitTime || '',
        consultationDuration: initialFilters.consultationDuration || '',
        isaccepted: initialFilters.isaccepted || '',
        createdAt: initialFilters.createdAt || '',
        updatedAt: initialFilters.updatedAt || '',
        // Patient demographics
        ageFrom: initialFilters.ageFrom || '',
        ageTo: initialFilters.ageTo || '',
        gender: initialFilters.gender || ''
    });

    // Handle click outside to close filter
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterRef.current && !filterRef.current.contains(event.target) && isVisible && onClose) {
                onClose();
            }
        };

        if (isVisible) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isVisible, onClose]);

    useEffect(() => {
        setFilters(prev => ({
            ...prev,
            ...initialFilters
        }));
    }, [initialFilters]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        let newFilters = { ...filters, [name]: value };
        
        // Handle date range logic
        if (name === 'dateRange') {
            const today = new Date().toISOString().split('T')[0];
            const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            const oneMonthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            
            switch (value) {
                case 'today':
                    newFilters.dateFrom = today;
                    newFilters.dateTo = today;
                    break;
                case 'week':
                    newFilters.dateFrom = oneWeekAgo;
                    newFilters.dateTo = today;
                    break;
                case 'month':
                    newFilters.dateFrom = oneMonthAgo;
                    newFilters.dateTo = today;
                    break;
                case 'specific':
                    newFilters.dateFrom = '';
                    newFilters.dateTo = '';
                    break;
                case 'custom':
                    // Keep existing dates or clear them
                    break;
            }
        }
        
        setFilters(newFilters);
        onFilterChange(newFilters);
    };

    const resetFilters = () => {
        const resetState = {
            id: '',
            appointmentId: '',
            patientName: '',
            patientAvatar: '',
            dateOfBirth: '',
            address: '',
            userid: '',
            meetinguserid: '',
            doctorName: '',
            doctorRole: '',
            date: '',
            dateRange: 'specific',
            dateFrom: '',
            dateTo: '',
            startTime: '',
            endTime: '',
            status: '',
            medicalHistory: '',
            appointmentType: '',
            notes: '',
            checkInTime: '',
            waitTime: '',
            consultationDuration: '',
            isaccepted: '',
            createdAt: '',
            updatedAt: '',
            ageFrom: '',
            ageTo: '',
            gender: ''
        };

        setFilters(resetState);
        onFilterChange(resetState);
    };

    // Get active filters for display as chips
    const getActiveFilters = () => {
        const activeFilters = [];
        
        Object.entries(filters).forEach(([key, value]) => {
            if (value && value !== '' && key !== 'dateRange') {
                let displayValue = value;
                let displayName = key;
                
                // Format display names
                switch (key) {
                    case 'patientName':
                        displayName = 'Patient';
                        break;
                    case 'doctorName':
                        displayName = 'Doctor';
                        break;
                    case 'appointmentType':
                        displayName = 'Type';
                        break;
                    case 'dateFrom':
                        displayName = 'From';
                        break;
                    case 'dateTo':
                        displayName = 'To';
                        break;
                    case 'ageFrom':
                        displayName = 'Min Age';
                        break;
                    case 'ageTo':
                        displayName = 'Max Age';
                        break;
                    default:
                        displayName = key.charAt(0).toUpperCase() + key.slice(1);
                }
                
                activeFilters.push({ key, displayName, value: displayValue });
            }
        });
        
        return activeFilters;
    };

    const removeFilter = (filterKey) => {
        const newFilters = { ...filters, [filterKey]: '' };
        setFilters(newFilters);
        onFilterChange(newFilters);
    };

    // Don't render if not visible
    if (!isVisible) {
        return null;
    }

    const activeFilters = getActiveFilters();

    return (
        <div className="card" ref={filterRef}>
            <div className="card-header d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Appointment Filters</h6>
                {onClose && (
                    <button 
                        type="button" 
                        className="btn-close" 
                        aria-label="Close"
                        onClick={onClose}
                    ></button>
                )}
            </div>

            {/* Active Filters Chips */}
            {activeFilters.length > 0 && (
                <div className="card-header bg-light border-bottom">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <small className="text-muted me-2">Active filters:</small>
                        {activeFilters.map(filter => (
                            <span 
                                key={filter.key}
                                className="badge bg-primary d-flex align-items-center gap-1"
                                style={{ fontSize: '0.75rem' }}
                            >
                                {filter.displayName}: {filter.value}
                                <button
                                    type="button"
                                    className="btn-close btn-close-white ms-1"
                                    style={{ fontSize: '0.6em' }}
                                    onClick={() => removeFilter(filter.key)}
                                    aria-label="Remove filter"
                                ></button>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <div className="card-body">
                <div className="row">
                    <div className="col-md-6 mb-3">
                        <label className="form-label">ID</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="id"
                            value={filters.id}
                            onChange={handleInputChange}
                            placeholder="Filter by ID"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Appointment ID</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="appointmentId"
                            value={filters.appointmentId}
                            onChange={handleInputChange}
                            placeholder="Filter by Appointment ID"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Patient Name</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="patientName"
                            value={filters.patientName}
                            onChange={handleInputChange}
                            placeholder="Filter by Patient Name"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Patient Avatar</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="patientAvatar"
                            value={filters.patientAvatar}
                            onChange={handleInputChange}
                            placeholder="Filter by Patient Avatar"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Date of Birth</label>
                        <input 
                            type="date" 
                            className="form-control" 
                            name="dateOfBirth"
                            value={filters.dateOfBirth}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Address</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="address"
                            value={filters.address}
                            onChange={handleInputChange}
                            placeholder="Filter by Address"
                        />
                    </div>

                    {/* Patient Demographics */}
                    <div className="col-md-6 mb-3">
                        <label className="form-label">Gender</label>
                        <select 
                            className="form-control" 
                            name="gender"
                            value={filters.gender}
                            onChange={handleInputChange}
                        >
                            <option value="">All Genders</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Age Range</label>
                        <div className="d-flex gap-2">
                            <input 
                                type="number" 
                                className="form-control" 
                                name="ageFrom"
                                value={filters.ageFrom}
                                onChange={handleInputChange}
                                placeholder="From"
                                min="0"
                                max="150"
                            />
                            <input 
                                type="number" 
                                className="form-control" 
                                name="ageTo"
                                value={filters.ageTo}
                                onChange={handleInputChange}
                                placeholder="To"
                                min="0"
                                max="150"
                            />
                        </div>
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">User ID</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="userid"
                            value={filters.userid}
                            onChange={handleInputChange}
                            placeholder="Filter by User ID"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Meeting User ID</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="meetinguserid"
                            value={filters.meetinguserid}
                            onChange={handleInputChange}
                            placeholder="Filter by Meeting User ID"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Doctor Name</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="doctorName"
                            value={filters.doctorName}
                            onChange={handleInputChange}
                            placeholder="Filter by Doctor Name"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Doctor Role</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="doctorRole"
                            value={filters.doctorRole}
                            onChange={handleInputChange}
                            placeholder="Filter by Doctor Role"
                        />
                    </div>

                    {/* Date Range Options */}
                    <div className="col-md-6 mb-3">
                        <label className="form-label">Date Range</label>
                        <select 
                            className="form-control" 
                            name="dateRange"
                            value={filters.dateRange}
                            onChange={handleInputChange}
                        >
                            <option value="specific">Specific Date</option>
                            <option value="today">Today</option>
                            <option value="week">Last 7 Days</option>
                            <option value="month">Last 30 Days</option>
                            <option value="custom">Custom Range</option>
                        </select>
                    </div>

                    {filters.dateRange === 'specific' && (
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Date</label>
                            <input 
                                type="date" 
                                className="form-control" 
                                name="date"
                                value={filters.date}
                                onChange={handleInputChange}
                            />
                        </div>
                    )}

                    {filters.dateRange === 'custom' && (
                        <>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">From Date</label>
                                <input 
                                    type="date" 
                                    className="form-control" 
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">To Date</label>
                                <input 
                                    type="date" 
                                    className="form-control" 
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </>
                    )}

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Start Time</label>
                        <input 
                            type="time" 
                            className="form-control" 
                            name="startTime"
                            value={filters.startTime}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">End Time</label>
                        <input 
                            type="time" 
                            className="form-control" 
                            name="endTime"
                            value={filters.endTime}
                            onChange={handleInputChange}
                        />
                    </div>

                    {/* Updated Status with required options */}
                    <div className="col-md-6 mb-3">
                        <label className="form-label">Status</label>
                        <select 
                            className="form-control" 
                            name="status"
                            value={filters.status}
                            onChange={handleInputChange}
                        >
                            <option value="">All Statuses</option>
                            <option value="Pending">Pending</option>
                            <option value="Waiting">Waiting</option>
                            <option value="Called">Called</option>
                            <option value="In Consultation">In Consultation</option>
                            <option value="Completed">Completed</option>
                            <option value="No-show">No-show</option>
                        </select>
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Medical History</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="medicalHistory"
                            value={filters.medicalHistory}
                            onChange={handleInputChange}
                            placeholder="Filter by Medical History"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Appointment Type</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="appointmentType"
                            value={filters.appointmentType}
                            onChange={handleInputChange}
                            placeholder="Filter by Appointment Type"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Notes</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="notes"
                            value={filters.notes}
                            onChange={handleInputChange}
                            placeholder="Filter by Notes"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Check-in Time</label>
                        <input 
                            type="datetime-local" 
                            className="form-control" 
                            name="checkInTime"
                            value={filters.checkInTime}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Wait Time (minutes)</label>
                        <input 
                            type="number" 
                            className="form-control" 
                            name="waitTime"
                            value={filters.waitTime}
                            onChange={handleInputChange}
                            placeholder="Filter by Wait Time"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Consultation Duration (minutes)</label>
                        <input 
                            type="number" 
                            className="form-control" 
                            name="consultationDuration"
                            value={filters.consultationDuration}
                            onChange={handleInputChange}
                            placeholder="Filter by Consultation Duration"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Is Accepted</label>
                        <select 
                            className="form-control" 
                            name="isaccepted"
                            value={filters.isaccepted}
                            onChange={handleInputChange}
                        >
                            <option value="">All</option>
                            <option value="true">Accepted</option>
                            <option value="false">Not Accepted</option>
                        </select>
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Created At</label>
                        <input 
                            type="date" 
                            className="form-control" 
                            name="createdAt"
                            value={filters.createdAt}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Updated At</label>
                        <input 
                            type="date" 
                            className="form-control" 
                            name="updatedAt"
                            value={filters.updatedAt}
                            onChange={handleInputChange}
                        />
                    </div>
                </div>

                <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                        {activeFilters.length} filter{activeFilters.length !== 1 ? 's' : ''} active
                    </small>
                    <div className="d-flex gap-2">
                        <button 
                            className="btn btn-outline-secondary" 
                            onClick={resetFilters}
                        >
                            Clear All Filters
                        </button>
                        {onClose && (
                            <button 
                                className="btn btn-primary" 
                                onClick={onClose}
                            >
                                Apply Filters
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppointmentFilter;